import { PropsWithChildren, ReactNode, createContext, useContext } from 'react';

import { AnyStepModel, StepModel } from './use-steps';

const StepModelContext = createContext<StepModel<AnyStepModel>>({
  step: 'initial',
  context: undefined,
});

const useStepModel = <T extends AnyStepModel>() => {
  return useContext(StepModelContext) as StepModel<T>;
};

export interface StepsProps<T extends AnyStepModel> {
  model: StepModel<T>;
}

const StepsBase = <T extends AnyStepModel>({ model, children }: PropsWithChildren<StepsProps<T>>) => {
  return <StepModelContext value={model as StepModel<AnyStepModel>}>{children}</StepModelContext>;
};

export interface StepProps<T extends AnyStepModel, N extends keyof T> {
  step: N;
  children: (model: Extract<StepModel<T>, { step: N }>['context']) => ReactNode;
}

const Step = <T extends AnyStepModel, const N extends keyof T>({ step, children }: StepProps<T, N>) => {
  const model = useStepModel<T>();

  if (model.step !== step) return null;
  return children(model.context);
};

type StepComponent<T extends AnyStepModel> = ((props: PropsWithChildren<StepsProps<T>>) => ReactNode) & {
  Step: <const N extends keyof T>(props: StepProps<T, N>) => ReactNode;
};

/**
 * @example
 *
 * ```tsx
 * type ModelOrder = {
 *   initial: undefined;
 *   second: { value: number };
 * };
 * const Steps = createStepComponent<ModelOrder>();
 * const Form = () => {
 *   const { current } = useSteps<ModelOrder>({
 *     initialValue: { step: 'initial', context: undefined }
 *   });
 *
 *   return (
 *     <Steps model={current}>
 *       <Steps.Step step='initial'>{() => <Intro />}</Steps.Step>
 *       <Steps.Step step='second'>{({ value }) => value.toFixed(2)}</Steps.Step>
 *     </Steps>
 *   );
 * };
 * ```
 */

export function createStepComponent<T extends AnyStepModel>(): StepComponent<T> {
  return Object.assign(StepsBase, { Step }) as unknown as StepComponent<T>;
}
